'use strict';

import {
	Path,
	Vector2,
	LineCurve,
} from 'three';

import { isPerpendicularLines } from '../functions';

export class Polygon extends Path{
	constructor (points) {
		super(points);
		
		this.center = new Vector2();
		this.autoClose = true;
		
		if (points) {
			this.closePath();
			this.currentPoint.set(points[0].x,points[0].y);
			this.getCenter();
		}
	}
	
	lineTo( x, y ) {
		
		const v1 = this.currentPoint.clone();
		const v2 = new Vector2( x, y );
		
		function outwardNormal(vec1, vec2) {
			const dx = vec2.x - vec1.x;
			const dy = vec2.y - vec1.y;
			const edgeLength = Math.sqrt(dx * dx + dy * dy);
			
			return new Vector2(-dy / edgeLength,dx / edgeLength);
		}
		
		function inwardNormal (vec1,vec2) {
			const n = outwardNormal(vec1, vec2);
			return n.negate();
		}
		
		
		const curve = new LineCurve(v1,v2);
		curve.outwardNormal = outwardNormal(v1,v2);
		curve.inwardNormal = inwardNormal(v1,v2);
		this.curves.push( curve );
		
		this.currentPoint.set( x, y );
		
		return this;
	}
	
	getCenter() {
		this.center.set(0,0);
		let signedArea = 0;
		let x0 = 0;
		let y0 = 0;
		let x1 = 0;
		let y1 = 0;
		let a = 0;
		
		const points = this.getPoints();
		
		for (let i = 0; i < points.length; i++) {
			x0 = points[i].x;
			y0 = points[i].y;
			x1 = points[(i + 1) % points.length].x;
			y1 = points[(i + 1) % points.length].y;
			a = x0 * y1 - x1 * y0;
			signedArea += a;
			this.center.x += (x0 + x1) * a;
			this.center.y += (y0 + y1) * a;
		}
		
		signedArea *= 0.5;
		this.center.x /= (6 * signedArea);
		this.center.y /= (6 * signedArea);
		
		return this.center;
	}
	
	offset(value,arcSegments = 0, checkCrossLines = false) {
		const originPoints = this.getPoints();
		let offset = value;
		
		if (value > 0) {
			this.margin(value,arcSegments);
		} else {
			this.padding(-value,arcSegments);
			
			if (checkCrossLines) {
				
				while (Polygon.isCrossLines(this)) {
					offset += 1;
					this.curves = [];
					delete this.cacheLengths;
					this.setFromPoints(originPoints);
					this.padding( -offset );
				}
			}
		}
		
		return offset;
	}
	
	setLength(length) {
		const scale = length / this.getLength();
		const points = [];
		
		for (let i = 0; i < this.curves.length; i++) {
			const curve = this.curves[i];
			points.push(new Vector2().copy(curve.v1).multiplyScalar(scale));
		}
		
		const start = points[0];
		const end = points[points.length - 1];
		
		if (!start.equals(end)) {
			points.push(start);
		}
		
		this.curves = [];
		delete this.cacheLengths;
		this.setFromPoints(points);
		this.getCenter();
		this.getLength();
	}
	
	moveToCenter(center) {
		this.getCenter();
		
		const direction = new Vector2().subVectors(center,this.center);
		const points = this.getPoints();
		
		points.forEach(point => {
			point.add(direction);
		});
		
		this.curves = [];
		delete this.cacheLengths;
		this.setFromPoints(points);
		this.getCenter();
	}
	
	getCurvesFromCurrentPoint(vec2 = this.currentPoint) {
		const curves = [];
		
		for (let i = 0; i < this.curves.length; i++) {
			const curve = this.curves[i];
			if (this.calcIsInsideThickLineSegment(curve.v1,curve.v2,vec2)) {
				curves.push(curve);
			}
		}
		
		return curves;
	}
	
	isCorner(vec2) {
		const curves = this.getCurvesFromCurrentPoint(vec2);
		return curves.length === 2;
	}
	
	getFullDistance(vec2) {
		const lengths = this.getCurveLengths();
		const curve = this.findCurveFromVec(vec2);
		if (curve) {
			const index = this.curves.indexOf(curve);
			let pointDistance = curve.v1.distanceTo(vec2);
			
			if (index > 0) {
				pointDistance += lengths[index - 1];
			}
			
			return pointDistance;
		}
	}
	
	getDistance(vec2) {
		let distance = Infinity;
		const isCorner = this.isCorner(vec2);
		if (isCorner) {
			const curves = this.getCurvesFromCurrentPoint(vec2);
			
			for (let i = 0; i < curves.length; i++) {
				const curve = curves[i];
				const d1 = curve.v1.distanceTo(vec2);
				const d2 = curve.v2.distanceTo(vec2);
				const dis = Math.min(d1,d2);
				if (dis < distance) {
					distance = dis;
				}
			}
		} else {
			const curve = this.pointOnSegment(vec2);
			const d1 = curve.v1.distanceTo(vec2);
			const d2 = curve.v2.distanceTo(vec2);
			distance = Math.min(d1,d2);
		}
		
		return distance;
	}
	
	multiplyScalar(value,customCenter) {
		const center = customCenter || this.getCenter();
		const points = this.getPoints();
		const scaledPoints = [];
		
		for (let i = 0; i < points.length; i++) {
			const point = points[i];
			const direction = new Vector2().subVectors(point,center);
			scaledPoints.push(new Vector2().addVectors(center,direction.multiplyScalar(value)));
		}
		this.curves = [];
		delete this.cacheLengths;
		this.setFromPoints(scaledPoints);
	}
	
	dotProduct(v1,v2,v3) {
		return (v2.x - v1.x) * (v3.x - v2.x) + (v2.y - v1.y) * (v3.y - v2.y);
	}
	
	crossProduct(v1,v2,v3) {
		return (v2.x - v1.x) * (v3.y - v2.y) - (v2.y - v1.y) * (v3.x - v2.x);
	}
	
	pointOnSegment(vec2) {
		let segment = null;
		
		for (let i = 0; i < this.curves.length; i++) {
			
			const curve = this.curves[i];
			if (this.calcIsInsideThickLineSegment(curve.v1,curve.v2,vec2)) {
				segment = curve;
				break;
			}
		}
		
		return segment;
	}
	
	getPointT(vec2) {
		const lengths = this.getCurveLengths();
		const curve = this.findCurveFromVec(vec2);
		if (curve) {
			const index = this.curves.indexOf(curve);
			let pointDistance = curve.v1.distanceTo(vec2);
			
			if (index > 0) {
				pointDistance += lengths[index - 1];
			}
			
			return pointDistance / lengths[lengths.length - 1];
		}
	}
	
	findCurveFromVec(vec) {
		let curve = null;
		
		for (let i = 0; i < this.curves.length; i++) {
			const currentCurve = this.curves[i];
			const onSegment = this.calcIsInsideThickLineSegment(currentCurve.v1,currentCurve.v2,vec,0.01);
			if (onSegment) {
				curve = currentCurve;
				break;
			}
		}
		
		return curve;
	}
	
	getPointsFromRange(v1,v2,cp) {
		let points = [];
		const pathCwi = [];
		const pathCcwi = [];
		
		const curve1 = this.findCurveFromVec(v1);
		const curve2 = this.findCurveFromVec(v2);
		const startIndex = this.curves.indexOf(curve1);
		const endIndex = this.curves.indexOf(curve2);
		
		let cwi = startIndex;
		let ccwi = startIndex;
		
		function _getPoints () {
			const pathClockwise = new Path(pathCwi);
			const pathCounterclockwise = new Path(pathCcwi);
			
			const centerClockwise = pathClockwise.getPoint(0.5);
			const centerCounterclockwise = pathCounterclockwise.getPoint(0.5);
			const distClockwise = centerClockwise.distanceTo(cp);
			const distCounterclockwise = centerCounterclockwise.distanceTo(cp);
			
			if (distClockwise < distCounterclockwise) {
				points = pathCwi;
			} else {
				points = pathCcwi;
			}
			
			return points;
		}
		
		if (startIndex === endIndex) {
			const cpCurve = this.findCurveFromVec(cp);
			
			if (cpCurve) {
				pathCcwi.push(v1,v2);
				while (true) {
					const current = this.curves[cwi];
					const next = this.curves[(cwi + 1) % this.curves.length];
					cwi = this.curves.indexOf(next);
					
					if (this.calcIsInsideThickLineSegment(current.v1,current.v2,v1)) {
						pathCwi.push(v1, current.v2);
					} else {
						pathCwi.push(current.v1,current.v2);
					}
					
					if (cwi === endIndex) {
						break;
					}
				}
			} else {
				const t1 = this.getPointT(v1);
				const t2 = this.getPointT(v2);
				
				if (t1 > t2) {
					pathCcwi.push(v1,v2);
					while (true) {
						const current = this.curves[cwi];
						const next = this.curves[(cwi + 1) % this.curves.length];
						cwi = this.curves.indexOf(next);
						
						if (this.calcIsInsideThickLineSegment(current.v1,current.v2,v1)) {
							pathCwi.push(v1, current.v2);
						} else {
							pathCwi.push(current.v1,current.v2);
						}
						
						if (cwi === endIndex) {
							break;
						}
					}
					
				} else {
					pathCwi.push(v1,v2);
					while (true) {
						const current = this.curves[ccwi];
						const prev = this.curves[(ccwi + this.curves.length - 1) % this.curves.length];
						ccwi = this.curves.indexOf(prev);
						
						if (this.calcIsInsideThickLineSegment(current.v1,current.v2,v1)) {
							pathCcwi.push(v1, current.v1);
						} else {
							pathCcwi.push(current.v2,current.v1);
						}
						
						if (ccwi === endIndex) {
							break;
						}
					}
				}
			}
			
			return _getPoints();
		}
		
		while (cwi !== endIndex) {
			const current = this.curves[cwi];
			const next = this.curves[(cwi + 1) % this.curves.length];
			cwi = this.curves.indexOf(next);
			
			if (this.calcIsInsideThickLineSegment(current.v1,current.v2,v1)) {
				pathCwi.push(v1, current.v2);
			} else {
				pathCwi.push(current.v1,current.v2);
			}
		}
		
		if (!pathCwi[pathCwi.length - 1].equals(v2)) {
			pathCwi.push(v2);
		}
		
		while (ccwi !== endIndex) {
			const current = this.curves[ccwi];
			const prev = this.curves[(ccwi + this.curves.length - 1) % this.curves.length];
			ccwi = this.curves.indexOf(prev);
			
			if (this.calcIsInsideThickLineSegment(current.v1,current.v2,v1)) {
				pathCcwi.push(v1, current.v1);
			} else {
				pathCcwi.push(current.v2,current.v1);
			}
		}
		
		if (!pathCcwi[pathCcwi.length - 1].equals(v2)) {
			pathCcwi.push(v2);
		}
		
		return _getPoints();
	}
	
	calcIsInsideThickLineSegment(v1, v2, pnt, lineThickness = 0.01) {
		const L2 = (((v2.x - v1.x) * (v2.x - v1.x)) + ((v2.y - v1.y) * (v2.y - v1.y)));
		if (L2 === 0) return false;
		const r = (((pnt.x - v1.x) * (v2.x - v1.x)) + ((pnt.y - v1.y) * (v2.y - v1.y))) / L2;
		
		if (r < 0) {
			//Outside line1
			return (Math.sqrt(((v1.x - pnt.x) * (v1.x - pnt.x)) + ((v1.y - pnt.y) * (v1.y - pnt.y))) <= lineThickness);
		} else if ((0 <= r) && (r <= 1)) {
			//On the line segment
			const s = (((v1.y - pnt.y) * (v2.x - v1.x)) - ((v1.x - pnt.x) * (v2.y - v1.y))) / L2;
			return (Math.abs(s) * Math.sqrt(L2) <= lineThickness);
		} else {
			//Outside line2
			return (Math.sqrt(((v2.x - pnt.x) * (v2.x - pnt.x)) + ((v2.y - pnt.y) * (v2.y - pnt.y))) <= lineThickness);
		}
	}
	
	
	margin(offset,arcSegments = 0) {
		const offsetCurves = [];
		
		for (let i = 0; i < this.curves.length; i++) {
			const curve = this.curves[i];
			const dx = curve.outwardNormal.x * offset;
			const dy = curve.outwardNormal.y * offset;
			offsetCurves.push(new LineCurve(
				new Vector2(curve.v1.x + dx,curve.v1.y + dy),
				new Vector2(curve.v2.x + dx,curve.v2.y + dy)
			));
		}
		
		const points = [];
		
		for (let i = 0; i < offsetCurves.length; i++) {
			const currentCurve = offsetCurves[i];
			const prevCurve = offsetCurves[(i + offsetCurves.length - 1) % offsetCurves.length];
			const intersect = Polygon.curvesIntersection(prevCurve,currentCurve);
			
			if (intersect && (!intersect.isIntersectionOutside || arcSegments < 1)) {
				points.push(intersect.vec);
			} else {
				const arcCenter = currentCurve.v1;
				Polygon.appendArc(
					arcSegments,
					points,
					arcCenter,
					offset,
					prevCurve.v2,
					currentCurve.v1,
					false
				);
			}
		}
		
		const start = points[0];
		const end = points[points.length - 1];
		
		if (!start.equals(end)) {
			points.push(start);
		}
		
		this.curves = [];
		delete this.cacheLengths;
		this.setFromPoints(points);
		this.getLength();
		this.getCenter();
	}
	
	padding(offset,arcSegments = 0) {
		const offsetCurves = [];
		
		for (let i = 0; i < this.curves.length; i++) {
			const curve = this.curves[i];
			const dx = curve.inwardNormal.x * offset;
			const dy = curve.inwardNormal.y * offset;
			offsetCurves.push(new LineCurve(
				new Vector2(curve.v1.x + dx,curve.v1.y + dy),
				new Vector2(curve.v2.x + dx,curve.v2.y + dy)
			));
		}
		
		let points = [];
		
		for (let i = 0; i < offsetCurves.length; i++) {
			const currentCurve = offsetCurves[i];
			const prevCurve = offsetCurves[(i + offsetCurves.length - 1) % offsetCurves.length];
			const intersect = Polygon.curvesIntersection(prevCurve,currentCurve);
			
			if (intersect && (!intersect.isIntersectionOutside || arcSegments < 1)) {
				points.push(intersect.vec);
			} else {
				const arcCenter = currentCurve.v1;
				Polygon.appendArc(
					arcSegments,
					points,
					arcCenter,
					offset,
					prevCurve.v2,
					currentCurve.v1,
					true
				);
			}
		}
		
		const start = points[0];
		const end = points[points.length - 1];
		
		if (!start.equals(end)) {
			points.push(start);
		}
		
		// Polygon.cropCrossLines(points);
		
		this.curves = [];
		delete this.cacheLengths;
		this.setFromPoints(points);
		this.getLength();
		this.getCenter();
	}
	
	intersects(vec2) {
		
		function closestPointOnSegment(point, vertex1, vertex2) {
			const segmentVector = vertex2.clone().sub(vertex1);
			const pointVector = point.clone().sub(vertex1);
			const segmentLengthSquared = segmentVector.lengthSq();
			const t = Math.max(0, Math.min(1, pointVector.dot(segmentVector) / segmentLengthSquared));
			return vertex1.clone().add(segmentVector.multiplyScalar(t));
		}
		
		let intersectedPoint = null;
		let minDistanceSquared = Infinity;
		const points = this.getPoints();
		
		for (let i = 0; i < points.length; i++) {
			let j = (i + 1) % points.length;
			const vertex1 = points[i];
			const vertex2 = points[j];
			const projection = closestPointOnSegment(vec2, vertex1, vertex2);
			const distanceSquared = projection.distanceToSquared(vec2);
			
			if (distanceSquared < minDistanceSquared) {
				minDistanceSquared = distanceSquared;
				intersectedPoint = projection;
			}
		}
		
		return intersectedPoint;
	}
	
	containsPoint(vec2) {
		let intersects = 0;
		const points = this.getPoints();
		
		for (let i = 0; i < points.length; i++) {
			const j = (i + 1) % points.length;
			const vertex1 = points[i];
			const vertex2 = points[j];
			if (((vertex1.y > vec2.y) !== (vertex2.y > vec2.y)) &&
				(vec2.x < (vertex2.x - vertex1.x) * (vec2.y - vertex1.y) / (vertex2.y - vertex1.y) + vertex1.x)) {
				intersects++;
			}
		}
		
		return intersects % 2 !== 0;
	}
	
	getPointFromDistance(distance) {
		let t;
		const l = this.getLength();
		
		if (distance > l) {
			t = (distance - l) / l;
			
			if (t > 1) {
				t = Math.abs(Math.trunc(t) - t);
			}
		} else if (distance < 0) {
			t = (l + distance) / l;
		} else {
			t = distance / l;
		}
		return this.getPoint(t);
	}
	
	static appendArc(arcSegments,points,center,radius,startPoint,endPoint,isPadding) {
		let startAngle = Math.atan2(
			startPoint.y - center.y,
			startPoint.x - center.x
		);
		let endAngle = Math.atan2(endPoint.y - center.y, endPoint.x - center.x);
		
		if (startAngle < 0) {
			startAngle += Math.PI * 2;
		}
		
		if (endAngle < 0) {
			endAngle += Math.PI * 2;
		}
		
		const angle =
			startAngle > endAngle
				? startAngle - endAngle
				: startAngle + Math.PI * 2 - endAngle;
		const angleStep = (isPadding ? -angle : Math.PI * 2 - angle) / arcSegments;
		
		points.push(startPoint);
		
		for (let i = 1; i < arcSegments; ++i) {
			const angle = startAngle + angleStep * i;
			
			points.push(new Vector2(
				center.x + Math.cos(angle) * radius,
				center.y + Math.sin(angle) * radius
			));
		}
		
		points.push(endPoint);
	}
	
	static isCrossLines(poly) {
		const points = poly.getPoints();
		const lines = [];
		let isCross = false;
		let intersection = null;
		
		for (let i = 0; i < points.length; i++) {
			const current = points[i];
			const next = points[(i + 1) % points.length];
			lines.push(new LineCurve(current,next));
		}
		
		function findCrossLines () {
			for (let i = 0; i < lines.length; i++) {
				const current = lines[i];
				const lineIntersection = Polygon.curvesIntersection(intersection,current);
				if (lineIntersection && lineIntersection.onLine1 && lineIntersection.onLine2) {
					isCross = true;
					break;
				}
			}
		}
		
		for (let i = 0; i < lines.length; i ++) {
			intersection = lines[i];
			findCrossLines(intersection);
			if (isCross) {
				break;
			}
		}
		
		return isCross;
	}
	
	static cropCrossLines(points) {
		const data = [];
		const lines = [];
		const skip = [];
		
		const poly = new Polygon(points);
		let intersection = null;
		
		for (let i = 0; i < points.length; i++) {
			const current = points[i];
			const next = points[(i + 1) % points.length];
			lines.push(new LineCurve(current,next));
		}
		
		function findCrossLines () {
			for (let i = 0; i < lines.length; i++) {
				const current = lines[i];
				const lineIntersection = Polygon.curvesIntersection(intersection,current);
				if (lineIntersection && lineIntersection.onLine1 && lineIntersection.onLine2) {
					const d1 = intersection.v1.distanceTo(poly.center);
					const d2 = intersection.v2.distanceTo(poly.center);
					const intersectionIndex = lines.indexOf(intersection);
					skip.push((intersectionIndex + i) / 2);
					
					data.push({
						v: d1 < d2 ? 'v2' : 'v1',
						i: intersectionIndex,
						intersection: lineIntersection.vec
					});
				}
			}
		}
		
		for (let i = 0; i < lines.length; i ++) {
			intersection = lines[i];
			findCrossLines(intersection);
		}
		
		if (data.length) {
			const points = [];
			
			for (let i = 0; i < lines.length; i ++) {
				if (skip.includes(i)) {
					continue;
				}
				const curve = lines[i];
				const intersectedData = data.find(el => el.i === i);
				if (intersectedData) {
					const point1 = curve.v1;
					const point2 = curve.v2;
					
					if (intersectedData.v === 'v1') {
						points.push(intersectedData.intersection,point2);
					} else {
						points.push(point1,intersectedData.intersection);
					}
					
				} else {
					points.push(curve.v1,curve.v2);
				}
			}
			
			return points;
			
		} else {
			return points;
		}
		
	}
	
	static simplifyPoints(points,tolerance) {
		function simplify(points, tolerance) {
			if (points.length <= 2) return points;
			
			const firstPoint = points[0];
			const lastPoint = points[points.length - 1];
			let maxDistance = 0;
			let index = 0;
			
			for (let i = 1; i < points.length - 1; i++) {
				const distance = perpendicularDistance(points[i], firstPoint, lastPoint);
				if (distance > maxDistance) {
					maxDistance = distance;
					index = i;
				}
			}
			
			if (maxDistance > tolerance) {
				const left = simplify(points.slice(0, index + 1), tolerance);
				const right = simplify(points.slice(index), tolerance);
				return left.slice(0, left.length - 1).concat(right);
			} else {
				return [firstPoint, lastPoint];
			}
		}
		
		function perpendicularDistance(point, lineStart, lineEnd) {
			const dx = lineEnd.x - lineStart.x;
			const dy = lineEnd.y - lineStart.y;
			const mag = Math.sqrt(dx * dx + dy * dy);
			const u = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (mag * mag);
			const x = lineStart.x + u * dx;
			const y = lineStart.y + u * dy;
			const dx1 = point.x - x;
			const dy1 = point.y - y;
			return Math.sqrt(dx1 * dx1 + dy1 * dy1);
		}
		
		return simplify(points, tolerance);
	}
	
	static triangulate(points) {
		const uniquePoints = Array.from(new Set(points.map(p => `${p.x},${p.y}`)))
			.map(str => {
				const [x, y] = str.split(',').map(Number);
				return new Vector2(x, y);
			});
		
		const center = uniquePoints.reduce((acc, point) => acc.add(point), new Vector2()).divideScalar(uniquePoints.length);
		const angleFromCenter = (point) => Math.atan2(point.y - center.y, point.x - center.x);
		uniquePoints.sort((a, b) => angleFromCenter(a) - angleFromCenter(b));
		
		return uniquePoints;
	}
	
	static curvesIntersection(curveA, curveB) {
		const onLines = {
			onLine1: false,
			onLine2: false
		};
		const den =
			(curveB.v2.y - curveB.v1.y) * (curveA.v2.x - curveA.v1.x) -
			(curveB.v2.x - curveB.v1.x) * (curveA.v2.y - curveA.v1.y);
		
		if (den === 0) {
			return null;
		}
		
		const ua =
			((curveB.v2.x - curveB.v1.x) * (curveA.v1.y - curveB.v1.y) -
				(curveB.v2.y - curveB.v1.y) *
				(curveA.v1.x - curveB.v1.x)) /
			den;
		
		const ub =
			((curveA.v2.x - curveA.v1.x) * (curveA.v1.y - curveB.v1.y) -
				(curveA.v2.y - curveA.v1.y) *
				(curveA.v1.x - curveB.v1.x)) /
			den;
		
		let a = curveA.v1.y - curveB.v1.y;
		let b = curveA.v1.x - curveB.v1.x;
		let numerator1 = ((curveB.v2.x - curveB.v1.x) * a) - ((curveB.v2.y - curveB.v1.y) * b);
		let numerator2 = ((curveA.v2.x - curveA.v1.x) * a) - ((curveA.v2.y - curveA.v1.y) * b);
		a = numerator1 / den;
		b = numerator2 / den;
		
		if (a > 0 && a < 1) {
			onLines.onLine1 = true;
		}
		
		if (b > 0 && b < 1) {
			onLines.onLine2 = true;
		}
		
		const isIntersectionOutside = ua < 0 || ub < 0 || ua > 1 || ub > 1;
		const intersection = new Vector2(
			curveA.v1.x + ua * (curveA.v2.x - curveA.v1.x),
			curveA.v1.y + ua * (curveA.v2.y - curveA.v1.y)
		);
		
		return {
			vec: intersection,
			isIntersectionOutside,
			...onLines
		};
	}
	
	static findPath(v1,v2,poly1,poly2,outPoly) {
		const curve = poly1.findCurveFromVec(v1);
		const curve2 = poly2.findCurveFromVec(v2);
		const isPerpendicular = isPerpendicularLines(curve.v1,curve.v2,v1,v2);
		const isPerpendicular2 = isPerpendicularLines(curve2.v1,curve2.v2,v1,v2);
		
		const direction = new Vector2().subVectors(v2,v1);
		
		if (isPerpendicular && isPerpendicular2) {
			return [v1,v2];
		} else {
			const distance = v1.distanceTo(v2);
			const center = new Vector2().copy(v1).add(direction.clone().normalize().multiplyScalar(distance / 2));
			
			let v3 = new Vector2(v1.x,center.y);
			let v4 = new Vector2(v2.x, center.y);
			
			if (!outPoly.containsPoint(v3) || !outPoly.containsPoint(v4) || outPoly.pointOnSegment(v3) || outPoly.pointOnSegment(v4)) {
				v3 = new Vector2(center.x,v1.y);
				v4 = new Vector2(center.x, v2.y);
			}
			
			return [v1,v3,v4,v2];
		}
	}
	
}
